import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, Eye, GripVertical, ImagePlus, MonitorPlay, X } from 'lucide-react';
import { toast } from 'sonner';
import CurriculumBuilder from '../../components/CurriculumBuilder';
import CourseStudents from './CourseStudents';
import RichText from '../../components/RichText';
import ImageAdjust from '../../components/ImageAdjust';
import { catalogAPI, coursesAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { t } from '../../lib/i18n';
import { Switch, adminBtnOutline, adminBtnPrimary, adminInput, statusBadge } from '../../components/adminUi';

// Admin 2 · Course editor (s134 redesign): breadcrumb + title + status + saved
// state + View course + Save; underlined tabs (Details · Curriculum N). Details
// = content column (Basics, Description, sortable Outcomes/Requirements) + a
// settings column (Publishing, Pricing, Media, Organization, SEO & tags).

// SVGs aren't canvas-friendly — they upload raw (adjust modal skipped).
const isSvg = (file) => file && (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name || ''));

const LEVELS = ['', 'beginner', 'intermediate', 'advanced'];
const LANGS = [['en', 'English'], ['es', 'Español']];
const PROVIDERS = ['', 'youtube', 'vimeo', 'url'];
const SHORT_MAX = 300;

const card = 'flex flex-col gap-4 rounded-xl border border-lms-line bg-lms-surface p-5 sm:px-6';
const h2 = 'text-base font-bold text-lms-ink';
const label = 'text-[13px] font-semibold text-lms-body';
const hint = 'text-xs text-lms-faint';

function F({ title, note, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className={label}>{title}</span>
      {children}
      {note && <span className={hint}>{note}</span>}
    </label>
  );
}

function SortableRow({ id, text, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
      className={`flex items-center gap-2.5 rounded-[10px] border border-lms-line bg-lms-raised px-2.5 py-2 ${isDragging ? 'shadow-[var(--lms-elev-2)]' : ''}`}
    >
      <button type="button" {...attributes} {...listeners} aria-label={t('Drag to reorder')} className="cursor-grab touch-none text-lms-faint hover:text-lms-ink">
        <GripVertical size={15} />
      </button>
      <span className="flex-1 text-sm text-lms-body">{text}</span>
      <button type="button" onClick={onRemove} aria-label={t('Remove')} className="flex h-8 w-8 items-center justify-center text-lms-faint hover:text-lms-sale">
        <X size={14} />
      </button>
    </div>
  );
}

// Ordered text list with drag-to-reorder (project standard: dnd-kit + grip).
function SortableList({ title, value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const items = value || [];
  const ids = items.map((_, i) => `i${i}`);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onChange(arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <section className={card}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className={h2}>{title}</h2>
        {items.length > 0 && <span className="text-[12.5px] text-lms-sub lms-num">{items.length} {t('items')} · {t('drag to reorder')}</span>}
      </div>
      {items.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2">
              {items.map((text, i) => (
                <SortableRow key={ids[i]} id={ids[i]} text={text} onRemove={() => onChange(items.filter((_, j) => j !== i))} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="flex gap-2.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          aria-label={placeholder}
          className={adminInput}
        />
        <button type="button" onClick={add} className={adminBtnOutline}>{t('Add')}</button>
      </div>
    </section>
  );
}

// Tags keep the compact chip style (order doesn't matter).
function Chips({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setDraft('');
  };
  return (
    <div className="flex flex-col gap-2">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={placeholder}
        aria-label={placeholder}
        className={adminInput}
      />
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, i) => (
            <span key={`${item}-${i}`} className="flex items-center gap-1 rounded-full border border-lms-line bg-lms-raised px-2.5 py-1 text-xs text-lms-body">
              {item}
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-lms-faint hover:text-lms-sale" aria-label={t('Remove')}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Segmented({ value, options, onChange, disabled }) {
  return (
    <div role="radiogroup" className="flex gap-1 rounded-[10px] bg-lms-raised p-1">
      {options.map(([v, l]) => {
        const on = value === v;
        const off = disabled && disabled(v);
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={off}
            onClick={() => onChange(v)}
            className={`h-[38px] flex-1 rounded-lg text-[13.5px] ${
              on ? 'bg-lms-surface font-bold text-lms-ink shadow-[var(--lms-elev-1)]' : 'font-semibold text-lms-sub hover:text-lms-ink'
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

// s136 discount: the admin picks a fixed amount ($) or a percentage (%) and
// types the value; the final price is previewed here and recomputed by the API.
const money2 = (n) => `$${(Math.round(Number(n || 0) * 100) / 100).toFixed(2)}`;

// Courses saved before s136 only have discounted_price → express it as an amount.
function legacyDiscountValue(course) {
  const p = Number(course.price) || 0;
  const d = Number(course.discounted_price) || 0;
  return course.discount_flag && d < p ? Math.round((p - d) * 100) / 100 : 0;
}

function finalPrice(price, type, value) {
  const p = Number(price) || 0;
  const v = Number(value) || 0;
  const out = type === 'percent' ? p * (1 - v / 100) : p - v;
  return Math.max(0, Math.round(out * 100) / 100);
}

function DiscountFields({ course, set }) {
  const type = course.discount_type || 'amount';
  const value = course.discount_value ?? legacyDiscountValue(course);
  const price = Number(course.price) || 0;
  const v = Number(value) || 0;
  const invalid = type === 'percent' ? !(v > 0 && v <= 100) : !(v > 0 && v <= price);
  const final = finalPrice(price, type, v);
  const opt = (key, label) => (
    <button
      type="button"
      role="radio"
      aria-checked={type === key}
      onClick={() => set({ discount_type: key })}
      className={`lms-press h-9 flex-1 rounded-[8px] text-[13px] ${
        type === key ? 'bg-lms-surface font-bold text-lms-ink shadow-[var(--lms-elev-1)]' : 'font-semibold text-lms-sub hover:text-lms-ink'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-col gap-3">
      <div role="radiogroup" aria-label={t('Discount type')} className="flex gap-1 rounded-[10px] bg-lms-raised p-1">
        {opt('amount', t('Fixed amount ($)'))}
        {opt('percent', t('Percentage (%)'))}
      </div>
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold text-lms-ink">
          {type === 'percent' ? t('Discount (%)') : t('Discount (USD)')}
        </span>
        <div className="relative">
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-lms-sub">
            {type === 'percent' ? '%' : '$'}
          </span>
          <input
            type="number"
            min="0"
            max={type === 'percent' ? 100 : undefined}
            step={type === 'percent' ? '1' : '0.01'}
            value={value}
            onChange={(e) => set({ discount_value: e.target.value })}
            aria-invalid={invalid}
            className={`${adminInput} pl-8`}
          />
        </div>
      </label>
      <div className="flex items-center justify-between rounded-[10px] border border-lms-line px-3.5 py-2.5">
        <span className="text-[13px] text-lms-sub">{t('Final price')}</span>
        <span className="lms-num text-sm">
          <span className="mr-2 text-lms-faint line-through">{money2(price)}</span>
          <span className="font-bold text-lms-ink">{money2(final)}</span>
        </span>
      </div>
      {invalid && (
        <span className="text-[12.5px] text-lms-sale">
          {type === 'percent' ? t('Enter a percentage between 1 and 100.') : t('Enter an amount greater than 0 and not above the price.')}
        </span>
      )}
    </div>
  );
}

export default function CourseEditor() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [cats, setCats] = useState([]);
  const [lessonCount, setLessonCount] = useState(null);
  const [params, setParams] = useSearchParams();
  const isAdmin = (getUser() || {}).lr === 'admin';
  // ?tab= keeps the open tab across the student detail page and back (s155)
  const tab = ['curriculum', 'students'].includes(params.get('tab')) && (params.get('tab') !== 'students' || isAdmin)
    ? params.get('tab') : 'details';
  const setTab = (k) => setParams(k === 'details' ? {} : { tab: k }, { replace: true });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  const staff = ['admin', 'manager'].includes((getUser() || {}).lr);

  useEffect(() => {
    Promise.all([coursesAPI.get(courseId), catalogAPI.list()])
      .then(([c, k]) => { setCourse(c.data); setCats(Array.isArray(k.data) ? k.data : k.data.categories || []); })
      .catch(() => toast.error(t('Could not load the course')));
    coursesAPI.curriculum(courseId)
      .then(({ data }) => setLessonCount((data.sections || data || []).reduce((n, s) => n + (s.lessons || []).length, 0)))
      .catch(() => {});
  }, [courseId]);

  // Leaving with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = useCallback((patch) => {
    setCourse((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const tops = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const subs = useMemo(
    () => cats.filter((c) => c.parent_id && c.parent_id === course?.category_id),
    [cats, course?.category_id]
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await coursesAPI.update(courseId, {
        title: course.title,
        short_description: course.short_description,
        description: course.description,
        outcomes: course.outcomes,
        requirements: course.requirements,
        language: course.language,
        category_id: course.category_id,
        sub_category_id: course.sub_category_id,
        level: course.level,
        price: course.price,
        discount_flag: course.discount_flag,
        discount_type: course.discount_type || 'amount',
        discount_value: course.discount_value ?? legacyDiscountValue(course),
        is_free: course.is_free,
        status: course.status,
        is_top: course.is_top,
        preview_video: course.preview_video,
        meta_keywords: course.meta_keywords,
        meta_description: course.meta_description,
        tags: course.tags,
      });
      setCourse(data);
      setDirty(false);
      toast.success(t('Saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const [adjustFile, setAdjustFile] = useState(null);
  const uploadThumb = async (file) => {
    try {
      const { data } = await coursesAPI.upload(courseId, 'thumbnail', file);
      setCourse((prev) => ({ ...prev, thumbnail: data.url }));
      toast.success(t('Thumbnail updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Upload failed'));
    }
  };
  const pickThumb = (e) => {
    const f = e.target.files[0];
    if (f) { if (isSvg(f)) uploadThumb(f); else setAdjustFile(f); }
    e.target.value = '';
  };

  if (!course) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  const statusOptions = [['draft', t('Draft')], ['pending', t('Pending')], ['active', t('Active')]];
  // Instructors can't publish; once active, staff own the status.
  const statusDisabled = (v) => !staff && (course.status === 'active' || v === 'active');
  const shortLen = (course.short_description || '').length;

  return (
    <div className="flex flex-col gap-6">
      {/* ===== HEADER ===== */}
      <div className="flex flex-col gap-[18px]">
        <nav aria-label={t('Breadcrumb')} className="flex items-center gap-2 text-[13.5px] font-semibold">
          <Link to="/admin/courses" className="text-lms-link hover:underline">{staff ? t('Courses') : t('My courses')}</Link>
          <ChevronRight size={14} className="text-lms-faint" />
          <span className="truncate text-lms-sub">{course.title}</span>
        </nav>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-[26px] font-bold leading-tight text-lms-ink">{course.title}</h1>
            {statusBadge(course.status)}
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className={`text-[13px] ${dirty ? 'font-semibold text-lms-tag-alt-ink' : 'text-lms-sub'}`}>
              {dirty ? t('Unsaved changes') : t('All changes saved')}
            </span>
            {/* s137: previews work in every status (draft too) for staff / the author. */}
            <a href={`/course/${course.slug}`} target="_blank" rel="noopener noreferrer" className={adminBtnOutline}
              title={t('The course page as students will see it')}>
              <Eye size={15} /> {t('Preview page')}
            </a>
            <a href={`/learn/${course.slug}`} target="_blank" rel="noopener noreferrer" className={adminBtnOutline}
              title={t('Take the course as a student: lessons, progress, checks')}>
              <MonitorPlay size={15} /> {t('Preview as student')}
            </a>
            <button type="button" onClick={save} disabled={saving || !dirty} className={adminBtnPrimary}>
              {saving ? t('Saving…') : t('Save changes')}
            </button>
          </div>
        </div>
        <div role="tablist" className="flex gap-7 border-b border-lms-line">
          {[['details', t('Details'), null], ['curriculum', t('Curriculum'), lessonCount],
            ...(isAdmin ? [['students', t('Students'), null]] : [])].map(([key, l, n]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-[3px] pb-3 text-[15px] ${
                tab === key ? 'border-lms-ink font-bold text-lms-ink' : 'border-transparent font-semibold text-lms-sub hover:text-lms-ink'
              }`}
            >
              {l}{typeof n === 'number' && <span className="ml-1.5 text-xs font-semibold text-lms-sub lms-num">{n}</span>}
            </button>
          ))}
        </div>
      </div>

      {tab === 'students' ? (
        <CourseStudents courseId={courseId} />
      ) : tab === 'curriculum' ? (
        <CurriculumBuilder courseId={courseId} courseSlug={course.slug} onCountChange={setLessonCount} />
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr),360px]">
          {/* ===== CONTENT COLUMN ===== */}
          <div className="flex min-w-0 flex-col gap-5">
            <section className={card}>
              <h2 className={h2}>{t('Basics')}</h2>
              <F title={t('Title')}>
                <input value={course.title} onChange={(e) => set({ title: e.target.value })} className={adminInput} />
              </F>
              <F title={t('Short description')} note={`${t('Shown on course cards and under the title')} · ${shortLen} / ${SHORT_MAX}`}>
                <textarea
                  value={course.short_description || ''}
                  onChange={(e) => set({ short_description: e.target.value.slice(0, SHORT_MAX) })}
                  rows={3}
                  className={`${adminInput} h-auto py-3 leading-relaxed`}
                />
              </F>
            </section>
            <section className={card}>
              <h2 className={h2}>{t('Description')}</h2>
              <RichText value={course.description} onChange={(html) => set({ description: html })} />
            </section>
            <SortableList
              title={t('What students will learn')}
              value={course.outcomes}
              onChange={(v) => set({ outcomes: v })}
              placeholder={t('Add a learning outcome…')}
            />
            <SortableList
              title={t('Requirements')}
              value={course.requirements}
              onChange={(v) => set({ requirements: v })}
              placeholder={t('Add a requirement…')}
            />
          </div>

          {/* ===== SETTINGS COLUMN ===== */}
          <div className="flex flex-col gap-5">
            <section className={card}>
              <h2 className={h2}>{t('Publishing')}</h2>
              <div className="flex flex-col gap-1.5">
                <span className={label}>{t('Status')}</span>
                <Segmented value={course.status} options={statusOptions} onChange={(v) => set({ status: v })} disabled={statusDisabled} />
                <span className={hint}>
                  {!staff && course.status !== 'active'
                    ? t('Set to "Pending" to submit for review — staff publish it.')
                    : t('Active courses are visible in the catalog.')}
                </span>
              </div>
              {staff && (
                <div className="flex items-center justify-between gap-3">
                  <span className="flex flex-col">
                    <span className={label}>{t('Top course')}</span>
                    <span className={hint}>{t('Featured in “Top courses” on the home.')}</span>
                  </span>
                  <Switch checked={!!course.is_top} onChange={(v) => set({ is_top: v ? 1 : 0 })} label={t('Top course')} />
                </div>
              )}
              <F title={t('Course URL')}>
                <div className={`${adminInput} flex items-center truncate text-lms-sub`}>
                  /course/<span className="truncate text-lms-ink">{course.slug}</span>
                </div>
              </F>
            </section>

            <section className={card}>
              <h2 className={h2}>{t('Pricing')}</h2>
              <div className="flex items-center justify-between gap-3">
                <span className={label}>{t('Free course')}</span>
                <Switch checked={!!course.is_free} onChange={(v) => set({ is_free: v ? 1 : 0 })} label={t('Free course')} />
              </div>
              {course.is_free ? (
                <span className="text-[12.5px] text-lms-sub">{t('Turn off to set a price and an optional discount.')}</span>
              ) : (
                <>
                  <F title={t('Price (USD)')}>
                    <input type="number" min="0" step="0.01" value={course.price} onChange={(e) => set({ price: e.target.value })} className={adminInput} />
                  </F>
                  <div className="flex items-center justify-between gap-3">
                    <span className={label}>{t('Discount active')}</span>
                    <Switch checked={!!course.discount_flag} onChange={(v) => set({ discount_flag: v ? 1 : 0 })} label={t('Discount active')} />
                  </div>
                  {!!course.discount_flag && (
                    <DiscountFields course={course} set={set} />
                  )}
                </>
              )}
            </section>

            <section className={card}>
              <h2 className={h2}>{t('Media')}</h2>
              {course.thumbnail ? (
                <div className="flex flex-col gap-2.5">
                  <img src={course.thumbnail} alt="" className="aspect-video w-full rounded-[10px] object-cover" />
                  <label className={`${adminBtnOutline} cursor-pointer`}>
                    <ImagePlus size={15} /> {t('Replace thumbnail')}
                    <input type="file" accept="image/*" className="hidden" onChange={pickThumb} />
                  </label>
                </div>
              ) : (
                <label className="flex aspect-video cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-dashed border-lms-pill p-4 text-center hover:border-lms-ink">
                  <ImagePlus size={26} className="text-lms-sub" />
                  <span className="text-sm font-bold text-lms-ink">{t('Upload thumbnail')}</span>
                  <span className="text-xs text-lms-sub">{t('JPG or PNG · 1600 × 900 recommended')}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={pickThumb} />
                </label>
              )}
              {adjustFile && (
                <ImageAdjust file={adjustFile} targetRatio={16 / 9} onCancel={() => setAdjustFile(null)}
                  onConfirm={(blobOrFile, filename) => { setAdjustFile(null); const payload = (blobOrFile instanceof File) ? blobOrFile : new File([blobOrFile], filename, { type: blobOrFile.type }); uploadThumb(payload); }} />
              )}
              <F title={t('Preview video')}>
                <select
                  value={course.preview_video?.provider || ''}
                  onChange={(e) => set({ preview_video: { ...course.preview_video, provider: e.target.value } })}
                  className={adminInput}
                >
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p ? p : t('None')}</option>)}
                </select>
              </F>
              {course.preview_video?.provider && (
                <F title={t('Preview URL')}>
                  <input
                    value={course.preview_video?.url || ''}
                    onChange={(e) => set({ preview_video: { ...course.preview_video, url: e.target.value } })}
                    className={adminInput}
                  />
                </F>
              )}
            </section>

            <section className={card}>
              <h2 className={h2}>{t('Organization')}</h2>
              <F title={t('Category')}>
                <select
                  value={course.category_id || ''}
                  onChange={(e) => set({ category_id: e.target.value || null, sub_category_id: null })}
                  className={adminInput}
                >
                  <option value="">{t('None')}</option>
                  {tops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </F>
              <F title={t('Sub-category')}>
                <select
                  value={course.sub_category_id || ''}
                  onChange={(e) => set({ sub_category_id: e.target.value || null })}
                  disabled={subs.length === 0}
                  className={`${adminInput} disabled:opacity-50`}
                >
                  <option value="">{t('None')}</option>
                  {subs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F title={t('Level')}>
                  <select value={course.level || ''} onChange={(e) => set({ level: e.target.value })} className={adminInput}>
                    {LEVELS.map((l) => <option key={l} value={l}>{l ? t(l) : t('Any level')}</option>)}
                  </select>
                </F>
                <F title={t('Language')}>
                  <select value={course.language || 'en'} onChange={(e) => set({ language: e.target.value })} className={adminInput}>
                    {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </F>
              </div>
            </section>

            <section className={`${card} gap-0`}>
              <button type="button" onClick={() => setSeoOpen((o) => !o)} aria-expanded={seoOpen} className="flex items-center justify-between">
                <h2 className={h2}>{t('SEO & tags')}</h2>
                <ChevronDown size={16} className={`text-lms-sub transition-transform ${seoOpen ? 'rotate-180' : ''}`} />
              </button>
              {seoOpen && (
                <div className="mt-4 flex flex-col gap-4">
                  <F title={t('Meta keywords')}>
                    <input value={course.meta_keywords || ''} onChange={(e) => set({ meta_keywords: e.target.value })} className={adminInput} />
                  </F>
                  <F title={t('Meta description')}>
                    <textarea value={course.meta_description || ''} onChange={(e) => set({ meta_description: e.target.value })} rows={3} className={`${adminInput} h-auto py-3`} />
                  </F>
                  <F title={t('Tags')}>
                    <Chips value={course.tags} onChange={(v) => set({ tags: v })} placeholder={t('Add a tag and press Enter')} />
                  </F>
                </div>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
